import React from 'react';
import Toggle from './Toggle.jsx';
import NumberInput, { NumericField } from './NumberInput.jsx';
import { DateTextInput } from './DateTextInput.jsx';
import ModifiedBadge, {
  isFXOverridden, isMultiplierOverridden, isJMRepayOverridden,
  OVERRIDE_REASON,
} from './ModifiedBadge.jsx';
import Select from './Select.jsx';
import { useToast } from './Toast.jsx';
import { PRESETS, INCOMES } from '../data.js';
import { getJMRepaySchedule, validateJMRepaySchedule, fmtMoney, detectFXCurrencies, resolveFXRate, calcJMPayDirectForMonth, ymLabel } from '../engine.js';
import { currencySymbol } from '../formatting.js';

/* yyyy-MM per E-002 — centralized via ymLabel. */
function formatLaunchMonth(ym) {
  return ym ? ymLabel(ym) : 'Not launching';
}

const PILL_BASE = 'inline-flex items-center text-[0.78rem] px-[14px] py-[5px] rounded-[14px] bg-surface-raised text-text-muted border border-border tracking-[0.04em] font-[inherit] cursor-pointer transition-all duration-150 hover:text-text hover:border-border-strong';
const PILL_ACTIVE = 'text-white bg-accent-blue border-accent-blue';
const PILL_SM = 'inline-flex items-center text-[0.7rem] px-[7px] py-px rounded-xl border cursor-default font-[inherit] tracking-[0.04em]';
const PILL_OK = 'bg-[rgba(34,197,94,0.15)] text-accent-green border-[rgba(34,197,94,0.35)]';
const PILL_WARN = 'bg-[rgba(251,191,36,0.12)] text-accent-gold border-[rgba(251,191,36,0.35)]';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const HINT_WARN = `${HINT} !border-l-accent-gold !text-text`;
const GROUP_TITLE = 'text-[0.78rem] text-accent-gold uppercase tracking-[0.08em] mb-[0.7rem] flex items-center gap-2 flex-wrap m-0';
const BTN_GHOST = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-[0.7rem] py-[0.45rem] rounded-sm font-[inherit] text-[0.82rem] cursor-pointer self-start hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const BTN_GHOST_SM = `${BTN_GHOST} !px-2 !py-[2px] !text-[11px]`;
const BTN_ICON = 'bg-surface-raised text-text-muted border border-border rounded-sm w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:border-red-soft';
const REPAY_INPUT = 'bg-surface-raised text-text border border-border rounded-sm px-2 py-[0.35rem] font-[inherit] text-[0.82rem] w-full focus:outline-none focus:border-accent-blue';
const LAUNCH_PILL_BASE = 'inline-flex items-center justify-center text-[0.78rem] px-[14px] py-[5px] rounded-[14px] bg-surface-raised text-text-muted border border-border tracking-[0.04em] font-[inherit] cursor-pointer transition-all duration-150 min-w-[68px] text-center tabular-nums font-semibold hover:text-text hover:border-border-strong';

export default function ScenarioPanel({ scenario, setScenario }) {
  const toast = useToast();
  const update = (patch) => setScenario({ ...scenario, ...patch });
  /* Applying a preset swaps the scenario's FINANCIAL inputs but must NOT
     wipe the user's UI/workspace state — saved profiles, active profile,
     visibility overrides, period-picker choices, etc. Preserve all of
     those explicitly by reading them off the current scenario before
     spreading the preset. */
  const applyPreset = (name) => {
    const p = PRESETS[name];
    setScenario({
      ...p,
      jmLoanRepaySchedule: scenario.jmLoanRepaySchedule,
      // workspace preferences
      profiles:            scenario.profiles,
      activeProfile:       null,  // preset is not a loaded profile
      sectionDensity:      scenario.sectionDensity,
      accountVisibility:   scenario.accountVisibility,
      accountTierFilter:   scenario.accountTierFilter,
      perTabTierFilter:    scenario.perTabTierFilter,
      formalStatementsOpen:scenario.formalStatementsOpen,
      formalPeriodCount:   scenario.formalPeriodCount,
      formalPeriodDensity: scenario.formalPeriodDensity,
      presentationSettings: scenario.presentationSettings,
      sparklineGradient:   scenario.sparklineGradient,
      // data the user has explicitly authored
      assetTransactions:   scenario.assetTransactions,
      adjustingEntries:    scenario.adjustingEntries,
      includeAdjustments:  scenario.includeAdjustments,
      // zb banner state
      zbBannerHidden:      scenario.zbBannerHidden,
      zbGapsAcknowledged:  scenario.zbGapsAcknowledged,
    });
  };

  const schedule = getJMRepaySchedule(scenario);
  const validation = validateJMRepaySchedule(schedule);
  const moneyPrefix = currencySymbol();

  const updateScheduleRow = (idx, patch) => {
    const next = schedule.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    update({ jmLoanRepaySchedule: next });
  };
  const addScheduleRow = () => {
    const last = schedule[schedule.length - 1];
    const next = [...schedule, { date: last?.date ?? '2026-06-20', amount: 0, account: last?.account ?? 'Demo Bank A' }];
    update({ jmLoanRepaySchedule: next });
  };
  const removeScheduleRow = (idx) => {
    const next = schedule.filter((_, i) => i !== idx);
    update({ jmLoanRepaySchedule: next });
  };

  return (
    <div className="bg-surface border border-border rounded-[10px] p-[1rem_1.15rem] mb-5 shadow-sm">
      <div className="flex justify-between items-center gap-3 mb-[0.8rem] pb-[0.6rem] border-b border-border flex-wrap">
        <h3 className="text-[0.95rem] font-semibold text-text tracking-[-0.01em] m-0">Shared Model Assumptions</h3>
        <div className="flex gap-[0.35rem] flex-wrap">
          {Object.keys(PRESETS).map((name) => {
            const active = scenario.name === PRESETS[name].name;
            return (
              <button
                key={name}
                className={`${PILL_BASE} ${active ? PILL_ACTIVE : ''}`}
                onClick={() => applyPreset(name)}
              >
                {PRESETS[name].name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4">
        {/* Core toggles */}
        <div className="bg-surface-el border border-border rounded-sm px-[0.9rem] py-[0.85rem]">
          <h4 className={GROUP_TITLE}>Operating Context</h4>
          <Toggle
            label="Primary income continuity"
            hint="OFF = resignation / job loss sim"
            checked={scenario.mtbContinuity}
            onChange={(v) => update({ mtbContinuity: v })}
          />
          <Toggle
            label="One-time financing received"
            hint="Expected on the configured financing date"
            checked={scenario.jmLoanReceived}
            onChange={(v) => update({ jmLoanReceived: v })}
          />
          <Toggle
            label="Dedicated stream kill switch"
            hint="OFF = zero out regardless of schedule. Use schedule below for amount + start/stop."
            checked={scenario.payDirectEnabled}
            onChange={(v) => update({ payDirectEnabled: v })}
          />
          {(() => {
            const line = INCOMES.jmPayDirect;
            if (!line) return null;
            const effectiveNow = calcJMPayDirectForMonth(scenario, (() => {
              const d = new Date();
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            })());
            const sched = scenario?.recurringOverrides?.[line.code]?.schedule ?? line.defaultSchedule;
            const steps = sched?.steps ?? [];
            const startYM = sched?.startYM ?? steps[0]?.fromYM ?? '—';
            return (
              <div className="flex items-center gap-3 py-2">
                <p className={`${HINT} flex-1 !m-0`}>
                  <strong className="text-text">{fmtMoney(effectiveNow, { decimals: 2 })}/mo</strong> from {startYM}
                  {scenario.payDirectEnabled === false && <em className="not-italic text-accent-red"> · kill switch ON</em>}
                  {steps.length > 1 && ` · ${steps.length} step-changes`}
                </p>
                <button
                  type="button"
                  className={BTN_GHOST_SM}
                  onClick={() => toast({
                    message: 'Edit the full schedule in Model Drivers -> Recurring Schedules',
                    tone: 'info',
                    duration: 2200,
                  })}
                >
                  Edit in Drivers →
                </button>
              </div>
            );
          })()}
        </div>

        {/* Contribution receivable toggles */}
        <div className="bg-surface-el border border-border rounded-sm px-[0.9rem] py-[0.85rem]">
          <h4 className={GROUP_TITLE}>Contribution Receivables</h4>
          <label className="flex flex-col gap-[0.3rem] py-[0.45rem]">
            <span className="text-[0.82rem] text-text flex justify-between items-baseline gap-[0.4rem]">Contributor A mode</span>
            <Select
              className="!flex w-full"
              value={scenario.jpMode || 'current'}
              onChange={(e) => update({ jpMode: e.target.value })}
            >
              <option value="current">Current</option>
              <option value="historical">Historical</option>
              <option value="custom">Custom…</option>
            </Select>
          </label>
          {scenario.jpMode === 'custom' && (
            <NumberInput
              label="Contributor A custom amount"
              value={scenario.jpAmount ?? 3500}
              prefix={moneyPrefix}
              onChange={(v) => update({ jpAmount: v })}
            />
          )}
          <NumberInput
            label="Contributor B monthly contribution"
            value={scenario.jcAmount}
            prefix={moneyPrefix}
            onChange={(v) => update({ jcAmount: v })}
          />
          <NumberInput
            label="Contributor C monthly contribution"
            value={scenario.jmAmount}
            prefix={moneyPrefix}
            onChange={(v) => update({ jmAmount: v })}
          />
        </div>

        {/* v7.1 — obligation multipliers */}
        <div className="bg-surface-el border border-border rounded-sm px-[0.9rem] py-[0.85rem]">
          <h4 className={GROUP_TITLE}>Obligation Multipliers</h4>
          <MarianSlider scenario={scenario} setScenario={setScenario} />
        </div>

        {/* recurring revenue */}
        <div className="bg-surface-el border border-border rounded-sm px-[0.9rem] py-[0.85rem]">
          <h4 className={GROUP_TITLE}>Recurring Revenue</h4>
          <div className="flex flex-col gap-[6px] my-[6px] mb-[10px]">
            <span className="text-[11px] uppercase tracking-[0.07em] text-text-muted">Launch month</span>
            <div className="flex flex-wrap gap-[6px]">
              {INCOMES.fvfRevenue.launchYM_options.map((o) => {
                const active = (scenario.fvfLaunchYM ?? null) === (o ?? null);
                const display = o ? formatLaunchMonth(o) : 'Not launching';
                const muted = !o;
                let state = '';
                if (active && !muted) {
                  state = 'bg-gradient-to-b from-[rgba(240,180,41,0.25)] to-[rgba(240,180,41,0.10)] border-accent-gold text-accent-gold shadow-[0_0_0_1px_rgba(240,180,41,0.20),0_2px_8px_rgba(240,180,41,0.08)]';
                } else if (active && muted) {
                  state = 'bg-[rgba(139,147,167,0.14)] border-text-muted text-text opacity-75';
                } else if (muted) {
                  state = 'opacity-75';
                }
                return (
                  <button
                    key={o ?? 'none'}
                    type="button"
                    className={`${LAUNCH_PILL_BASE} ${state}`}
                    onClick={() => update({ fvfLaunchYM: o ?? null })}
                    title={o ? `Subscription revenue goes live on ${o}` : 'Recurring stream disabled in this model state'}
                  >
                    {display}
                  </button>
                );
              })}
            </div>
          </div>
          <NumberInput
            label="Target recurring revenue"
            hint="Run-rate target once the recurring stream is mature; drives valuation views"
            value={scenario.fvfTargetMRR}
            prefix={moneyPrefix}
            onChange={(v) => update({ fvfTargetMRR: v })}
          />
          <NumberInput
            label="Short-term debt paydown % of free cash"
            value={scenario.ccPaydownPctOfFreeCash * 100}
            suffix="%"
            onChange={(v) => update({ ccPaydownPctOfFreeCash: (Number(v) || 0) / 100 })}
          />
        </div>

        {/* Loan repay piecewise */}
        <div className="bg-surface-el border border-border rounded-sm px-[0.9rem] py-[0.85rem] col-span-full">
          <h4 className={GROUP_TITLE}>
            Financing Piecewise Repayment
            <span className={`${PILL_SM} ${validation.valid ? PILL_OK : PILL_WARN}`}>
              {fmtMoney(validation.sum)} / {fmtMoney(validation.target)}
              {validation.valid ? ' ✓' : ` (${validation.delta > 0 ? '+' : ''}${fmtMoney(validation.delta)})`}
            </span>
          </h4>
          <div className="flex flex-col gap-[0.4rem] mt-1">
            <div className="grid grid-cols-[140px_120px_1fr_32px] gap-2 px-1 pb-[6px] border-b border-border mb-[6px]">
              <span className="text-[10px] uppercase tracking-[0.08em] text-text-muted font-semibold">Payment date</span>
              <span className="text-[10px] uppercase tracking-[0.08em] text-text-muted font-semibold">Amount</span>
              <span className="text-[10px] uppercase tracking-[0.08em] text-text-muted font-semibold" title="Which account the repayment is drawn from">Source account</span>
              <span className="text-[10px] uppercase tracking-[0.08em] text-text-muted font-semibold" aria-hidden="true"></span>
            </div>
            {schedule.map((row, idx) => (
              <div key={idx} className="grid grid-cols-[140px_120px_1fr_32px] gap-2 items-center mb-1">
                <DateTextInput
                  className={REPAY_INPUT}
                  value={row.date || ''}
                  onChange={(e) => updateScheduleRow(idx, { date: e.target.value })}
                />
                <NumericField
                  className={`${REPAY_INPUT} text-right tabular-nums`}
                  value={row.amount}
                  onChange={(v) => updateScheduleRow(idx, { amount: v })}
                  decimals={2}
                  min={0}
                />
                <input
                  type="text"
                  className={`${REPAY_INPUT} placeholder:text-text-dim placeholder:italic`}
                  value={row.account || ''}
                  placeholder="e.g. Demo Bank A · Demo Bank B"
                  title="The account the money leaves from when the repayment is made"
                  onChange={(e) => updateScheduleRow(idx, { account: e.target.value })}
                />
                <button className={BTN_ICON} onClick={() => removeScheduleRow(idx)} aria-label="Remove">✕</button>
              </div>
            ))}
            <button className={BTN_GHOST} onClick={addScheduleRow}>+ Add payment</button>
          </div>
          {!validation.valid && (
            <p className={HINT_WARN}>
              Payments must total exactly {fmtMoney(validation.target)}. Currently {fmtMoney(validation.delta)} {validation.delta > 0 ? 'over' : 'short'}.
            </p>
          )}
        </div>
      </div>

      <FXOverrideSection scenario={scenario} setScenario={setScenario} />
    </div>
  );
}

/* Routed obligation multiplier slider */
function MarianSlider({ scenario, setScenario }) {
  const value = Number(scenario?.multipliers?.marian ?? 1);
  const pct = Math.max(0, Math.min(100, ((value - 0.5) / (1.5 - 0.5)) * 100));
  const set = (v) => setScenario({
    ...scenario,
    multipliers: { ...(scenario.multipliers ?? {}), marian: Number(v) },
  });
  const presets = [0.75, 1.0, 1.25];
  return (
    <div className="flex flex-col gap-[0.4rem]">
      <label className="flex flex-col gap-[0.3rem] py-[0.45rem]">
        <span className="text-[0.82rem] text-text">
          Routed obligation multiplier
          {isMultiplierOverridden(scenario, 'marian') && <ModifiedBadge active reason={OVERRIDE_REASON.multiplier} />}
        </span>
        <div className="flex items-center gap-2">
          <NumericField
            aria-label="Routed obligation multiplier"
            className={`${REPAY_INPUT} max-w-[110px] text-right tabular-nums`}
            decimals={2}
            min={0.5}
            max={1.5}
            title="Type a multiplier between 0.50 and 1.50"
            value={value}
            onChange={set}
          />
          <span className="text-[0.82rem] text-text-muted">x</span>
        </div>
        <div
          className="relative h-1.5 w-full rounded-full bg-bg"
          aria-hidden="true"
        >
          <span
            className="absolute left-0 top-0 h-full rounded-full bg-accent-gold"
            style={{ width: `${pct}%` }}
          />
        </div>
      </label>
      <div className="flex items-center gap-[0.6rem]">
        <strong className="text-[1.2rem] text-accent-gold tabular-nums min-w-[4ch]">{value.toFixed(2)}×</strong>
        <div className="flex gap-[0.3rem] flex-wrap">
          {presets.map((p) => {
            const active = Math.abs(value - p) < 0.01;
            const state = active ? 'text-white bg-accent-blue border-accent-blue' : '';
            return (
              <button
                key={p}
                type="button"
                className={`inline-flex items-center text-[0.7rem] px-[7px] py-px rounded-xl bg-surface-raised text-text-muted border border-border cursor-pointer font-[inherit] tracking-[0.04em] hover:text-text hover:border-border-strong ${state}`}
                onClick={() => set(p)}
                title={`Snap to ${p.toFixed(2)}×`}
              >
                {p.toFixed(2)}×
              </button>
            );
          })}
        </div>
      </div>
      <p className="mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm">
        1.0× = baseline · &lt;1.0 = reduced obligation · &gt;1.0 = escalated.
        Drives routed obligation value across Budgets/Forecasts/Synthesis and the Tornado axis.
      </p>
    </div>
  );
}

/* FX Rates override */
function FXOverrideSection({ scenario, setScenario }) {
  const currencies = detectFXCurrencies();
  if (currencies.length === 0) return null;
  const ym = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const fxRates = scenario.fxRates || {};
  const setRate = (currency, rate) => {
    const next = { ...fxRates };
    if (rate == null || Number.isNaN(rate)) delete next[currency];
    else next[currency] = rate;
    setScenario({ ...scenario, fxRates: next });
  };
  return (
    <details className="mt-4">
      <summary className="cursor-pointer text-[0.85rem] text-text-muted hover:text-text">
        FX Rates Override · {currencies.length} {currencies.length === 1 ? 'currency' : 'currencies'} detected
      </summary>
      <p className="mt-2 text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm">
        Manual pin overrides the data.js baseRate + monthlyDrift drift.
        Empty / Reset = use drift formula.
      </p>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[0.82rem] mt-[0.4rem]">
        <thead>
          <tr>
            <th className="text-left font-medium text-text-dim px-[0.4rem] py-[0.3rem] border-b border-border">Currency</th>
            <th className="text-left font-medium text-text-dim px-[0.4rem] py-[0.3rem] border-b border-border">Drift rate now</th>
            <th className="text-left font-medium text-text-dim px-[0.4rem] py-[0.3rem] border-b border-border">Manual pin</th>
            <th className="text-left font-medium text-text-dim px-[0.4rem] py-[0.3rem] border-b border-border"></th>
          </tr>
        </thead>
        <tbody>
          {currencies.map((c) => {
            const driftRate = resolveFXRate({ currency: c, baseRate: 56.5, monthlyDrift: 0.002 }, { fxRates: {} }, ym);
            const pinned = fxRates[c];
            return (
              <tr key={c}>
                <td className="px-[0.4rem] py-[0.35rem] border-b border-dashed border-border">
                  <strong>{c}</strong>
                  {isFXOverridden(scenario, c) && <ModifiedBadge active reason={OVERRIDE_REASON.fx} />}
                </td>
                <td className="px-[0.4rem] py-[0.35rem] border-b border-dashed border-border"><em className="text-text-muted not-italic">{driftRate.toFixed(2)}</em></td>
                <td className="px-[0.4rem] py-[0.35rem] border-b border-dashed border-border">
                  <NumericField
                    value={pinned ?? null}
                    onChange={(v) => setRate(c, v == null || v === '' ? null : v)}
                    decimals={4}
                    min={0}
                    placeholder={driftRate.toFixed(2)}
                    className="w-[120px] bg-surface-raised border border-border-strong text-text px-[6px] py-[2px] rounded-[3px] font-[inherit] text-[0.8rem]"
                    title="Decimals + arithmetic (e.g. 56.8*1.02). Empty = drift formula"
                  />
                </td>
                <td className="px-[0.4rem] py-[0.35rem] border-b border-dashed border-border">
                  <button
                    type="button"
                    className={BTN_GHOST_SM}
                    onClick={() => setRate(c, null)}
                    disabled={pinned == null}
                  >
                    Reset
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </details>
  );
}
